import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';

import { AutoAuth } from './components/auth/AutoAuth.js';
import { PublicMapView } from './components/public/PublicMapView.js';
import { PublicLocationView } from './components/public/PublicLocationView.js';
import { MapCanvas } from './components/map/MapCanvas.js';
import { DrawingTools } from './components/map/DrawingTools.js';
import { Sidebar } from './components/sidebar/Sidebar.js';
import { DayTray } from './components/map/DayTray.js';
import { LocationInfoPanel } from './components/map/LocationInfoPanel.js';
import { Modal } from './components/ui/Modal.js';
import { ProjectForm } from './components/projects/ProjectForm.js';
import { SceneForm } from './components/scenes/SceneForm.js';
import { PinForm } from './components/pins/PinForm.js';
import { DayForm } from './components/days/DayForm.js';
import { useUiStore } from './stores/uiStore.js';
import { useMapStore } from './stores/mapStore.js';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function MapApp() {
  const { modal, closeModal } = useUiStore();
  const { activeProject, activeScene } = useMapStore();

  return (
    <div className="w-screen h-screen relative overflow-hidden bg-gray-100">
      {/* Map fills screen */}
      <MapCanvas />

      {/* Sidebar overlays left side */}
      <Sidebar />

      {/* Drawing toolbar centered at top of map */}
      <DrawingTools />

      {/* Location/pin info panel top-right */}
      <LocationInfoPanel />

      {/* Day/scene tray bottom-right */}
      <DayTray />

      {/* ---- Modals ---- */}
      <Modal
        open={modal.type === 'projectForm'}
        onClose={closeModal}
        title={modal.payload ? 'Edit project' : 'New project'}
        size="md"
      >
        <ProjectForm
          project={modal.type === 'projectForm' ? modal.payload as never : undefined}
          onClose={closeModal}
        />
      </Modal>

      <Modal
        open={modal.type === 'sceneForm'}
        onClose={closeModal}
        title={modal.payload && 'id' in (modal.payload as object) ? 'Edit scene' : 'New scene'}
        size="md"
      >
        {(modal.type === 'sceneForm') && (
          <SceneForm
            projectId={
              (modal.payload as { id?: string; projectId?: string })?.id
                ? (modal.payload as { projectId: string }).projectId
                : (modal.payload as { projectId: string })?.projectId ?? activeProject?.id ?? ''
            }
            scene={
              (modal.payload as { id?: string })?.id
                ? (modal.payload as never)
                : undefined
            }
            onClose={closeModal}
          />
        )}
      </Modal>

      <Modal
        open={modal.type === 'pinForm'}
        onClose={closeModal}
        title={(modal.payload as Pin | null)?.id ? 'Edit pin' : 'New pin'}
        size="md"
      >
        {(modal.type === 'pinForm') && (
          <PinForm
            sceneId={
              (modal.payload as { id?: string; sceneId?: string })?.id
                ? (modal.payload as { sceneId: string }).sceneId
                : (modal.payload as { sceneId: string })?.sceneId ?? activeScene?.id ?? ''
            }
            pin={(modal.payload as { id?: string })?.id ? (modal.payload as never) : undefined}
            onClose={closeModal}
          />
        )}
      </Modal>

      <Modal
        open={modal.type === 'dayForm'}
        onClose={closeModal}
        title={(modal.payload as { id?: string } | null)?.id ? 'Edit day' : 'New shoot day'}
        size="sm"
      >
        {(modal.type === 'dayForm') && (
          <DayForm
            projectId={
              (modal.payload as { id?: string; projectId?: string })?.id
                ? (modal.payload as { projectId: string }).projectId
                : (modal.payload as { projectId: string })?.projectId ?? activeProject?.id ?? ''
            }
            day={(modal.payload as { id?: string })?.id ? (modal.payload as never) : undefined}
            onClose={closeModal}
          />
        )}
      </Modal>
    </div>
  );
}

// Used for type narrowing in JSX
interface Pin { id: string; sceneId: string }

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/map/:token" element={<PublicMapView />} />
          <Route path="/location/:token" element={<PublicLocationView />} />
          <Route
            path="/*"
            element={
              <AutoAuth>
                <MapApp />
              </AutoAuth>
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" toastOptions={{ duration: 3000 }} />
    </QueryClientProvider>
  );
}
